import React, { useState } from "react";
import api from "@/lib/api";
import { Home, Send, CheckCircle2, MapPin, Phone, Mail, User, Ruler, Target } from "lucide-react";
import { useSeo } from "@/lib/seo";

const PROPERTY_TYPES = [
    { id: "appartamento", label: "Appartamento" },
    { id: "villa", label: "Villa" },
    { id: "locale_commerciale", label: "Locale commerciale" },
    { id: "terreno", label: "Terreno" },
    { id: "altro", label: "Altro" },
];

export default function VendiCasa() {
    useSeo({
        title: "Vendi casa: valutazione gratuita e agenzie della tua zona | VendiDirect",
        description: "Vuoi vendere o affittare casa? Lascia i dati del tuo immobile: ricevi una valutazione e vieni contattato dalle migliori agenzie della tua zona, senza impegno.",
        canonical: "/vendi-casa",
    });
    const [form, setForm] = useState({
        first_name: "", last_name: "", phone: "", email: "",
        property_address: "", city: "", property_type: "appartamento",
        surface_sqm: "", objective: "vendere", notes: "",
    });
    const [privacy, setPrivacy] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!privacy) { setError("Devi accettare la Privacy Policy per proseguire."); return; }
        setSending(true);
        try {
            await api.post("/vendi-casa/lead", {
                ...form,
                surface_sqm: form.surface_sqm ? parseFloat(form.surface_sqm) : null,
                privacy_accepted: true,
            });
            setSent(true);
        } catch (err) {
            setError(err?.response?.data?.detail || "Errore nell'invio. Riprova.");
        } finally { setSending(false); }
    };

    if (sent) {
        return (
            <div data-testid="vendi-casa-success" className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
                <CheckCircle2 size={64} className="text-gold mx-auto mb-6" />
                <h1 className="font-serif text-4xl text-ink-50 mb-4">Richiesta inviata</h1>
                <p className="text-lg text-ink-300 mb-2">Grazie, un&apos;agenzia della tua zona ti contatterà entro 24 ore.</p>
                <p className="text-sm text-ink-500">Nel frattempo puoi esplorare gli annunci già pubblicati sulla nostra piattaforma.</p>
                <a href="/annunci" className="cta-outline mt-8 inline-block px-6 py-3 text-sm">Sfoglia gli annunci</a>
            </div>
        );
    }

    const input = "w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none transition-colors text-sm";
    const label = "text-[11px] uppercase tracking-wider text-gold mb-1.5 flex items-center gap-1.5";

    return (
        <div data-testid="vendi-casa-page" className="pt-32 pb-24 max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
                <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-3 flex items-center justify-center gap-2">
                    <Home size={13} /> Vendi la tua casa gratis
                </div>
                <h1 className="font-serif text-4xl md:text-5xl text-ink-50 leading-tight mb-4">
                    Vuoi vendere o affittare casa?<br />
                    <span className="text-gold">Trova un&apos;agenzia nella tua zona</span>, gratuitamente.
                </h1>
                <p className="text-ink-300 max-w-2xl mx-auto">Compila il modulo qui sotto. Un&apos;agenzia locale certificata ti contatterà per una valutazione gratuita entro 24 ore.</p>
            </div>

            <form onSubmit={submit} data-testid="vendi-casa-form" className="border border-white/10 p-8 md:p-10 bg-ink-900/40 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={label}><User size={11} /> Nome *</label>
                        <input required data-testid="vc-first_name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><User size={11} /> Cognome *</label>
                        <input required data-testid="vc-last_name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><Phone size={11} /> Telefono *</label>
                        <input required type="tel" data-testid="vc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><Mail size={11} /> Email *</label>
                        <input required type="email" data-testid="vc-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} />
                    </div>
                    <div className="md:col-span-2">
                        <label className={label}><MapPin size={11} /> Indirizzo immobile *</label>
                        <input required data-testid="vc-property_address" placeholder="Es. Via Roma 12" value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><MapPin size={11} /> Città *</label>
                        <input required data-testid="vc-city" placeholder="Es. Milano" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><Home size={11} /> Tipologia immobile *</label>
                        <select required data-testid="vc-property_type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className={input}>
                            {PROPERTY_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={label}><Ruler size={11} /> Metratura (m²)</label>
                        <input type="number" min="0" data-testid="vc-surface_sqm" placeholder="Es. 90" value={form.surface_sqm} onChange={(e) => setForm({ ...form, surface_sqm: e.target.value })} className={input} />
                    </div>
                    <div>
                        <label className={label}><Target size={11} /> Obiettivo *</label>
                        <select required data-testid="vc-objective" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className={input}>
                            <option value="vendere">Vendere</option>
                            <option value="affittare">Affittare</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className={label}>Note aggiuntive</label>
                        <textarea rows={4} data-testid="vc-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={input} placeholder="Dettagli utili all'agenzia (piano, stato di manutenzione, urgenza...)" />
                    </div>
                </div>

                <label data-testid="vc-privacy-label" className="flex items-start gap-3 text-xs text-ink-300 cursor-pointer pt-2 border-t border-white/5">
                    <input type="checkbox" required data-testid="vc-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-gold shrink-0" />
                    <span>
                        Ho letto e accetto la <a href="/privacy" target="_blank" rel="noreferrer" className="text-gold underline">Privacy Policy</a> di VendiDirect (obbligatorio). I miei dati verranno condivisi solo con un&apos;agenzia della mia zona.
                    </span>
                </label>

                {error && <div data-testid="vc-error" className="text-red-300 text-xs border border-red-700/40 bg-red-900/20 px-3 py-2">{error}</div>}

                <button
                    type="submit"
                    data-testid="vc-submit"
                    disabled={sending || !privacy}
                    className="w-full cta-gold py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Send size={14} /> {sending ? "Invio in corso..." : "Ricevi una valutazione gratuita"}
                </button>
                <p className="text-[10px] text-ink-500 text-center">Servizio 100% gratuito per il proprietario. L&apos;agenzia riceverà i tuoi contatti e ti chiamerà entro 24 ore.</p>
            </form>
        </div>
    );
}
